/**
 * `HeaderBar` - the 56px bar above the content area.
 *
 * Per the MUR mockups: breadcrumb on the left, then right-aligned at 12px
 * gaps - the unavailable search field, the monogram avatar, and the staff
 * name over their role. White surface, hairline bottom border.
 *
 * Breadcrumb. This is the app's only breadcrumb. On a list screen it is the
 * section name alone; on a detail screen it becomes "Members / <account
 * number>" with the section as a link. Detail pages therefore do not render
 * a breadcrumb of their own - one trail, in the chrome, always in the same
 * place. The section name repeating the list page's `<h1>` is deliberate:
 * one is 14px grey chrome, the other a 28px page heading, and dropping the
 * `<h1>` to avoid the repetition would cost the page its accessible name.
 *
 * Nothing here is a heading. The header precedes the page `<h1>` in DOM
 * order, so an `<h*>` in this file produces an h2-before-h1 outline on
 * every screen - the exact defect the axe `best-practice` tag catches at
 * "moderate".
 *
 * Search. Built to the designed dimensions but inert: there is no search
 * endpoint at M1 (MUR Q12 - the search contract is unspecified). It renders
 * `disabled` with a visibly muted treatment and a `not-allowed` cursor,
 * plus an explanatory note wired through `aria-describedby` rather than a
 * `title` (which screen readers do not reliably surface). Earlier this was
 * `readOnly`, which looked and felt exactly like a working field - click,
 * caret, type, nothing. Being plainly unavailable beats being silently
 * broken. When the endpoint lands, drop `disabled` and wire `onSubmit`.
 *
 * The accessible name and the placeholder are the same string on purpose.
 * They used to disagree - the label said "Search members and applications"
 * while the field read "Search arrives in a later release" - which is the
 * regression this repo has now shipped twice in other guises.
 *
 * Identity. The avatar is the sign-out menu trigger (`SignOutMenu`); the
 * name and role beside it are static text. The monogram is built from the
 * signed-in **staff** name, which is Suits Me employee identity, not member
 * PII - the no-PII-in-chrome rule is about the people whose accounts staff
 * are looking at, and it is untouched here. (Worth stating, because the
 * next reader will see initials in permanent chrome and reach for that
 * rule.) No member name, number or identifier appears in this bar; the only
 * member-derived string is the breadcrumb trail, which is the account
 * number of a record the user has already opened and audited.
 */
import { useId } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { displayName } from '../auth/claims'
import { useAuth } from '../auth/use-auth'
import { sectionTitle } from '../lib/route-title'
import { useBreadcrumbTrail } from './breadcrumb'
import { SignOutMenu } from './sign-out-menu'
import { staffRoleLabel } from './staff-role-label'

export function HeaderBar(): JSX.Element {
  const { pathname } = useLocation()
  const { claims, signedIn } = useAuth()
  const name = signedIn ? displayName(claims) : null
  // One role, chosen by declared precedence, rendered in English. Null when
  // we cannot name any of them - see `staff-role-label.ts` and MUR Q3.
  const role = signedIn ? staffRoleLabel(claims?.roles ?? []) : null
  const id = useId()
  const searchNoteId = `${id}-search-note`

  // Set by the screen once its record loads; null on list and module screens.
  const trail = useBreadcrumbTrail()
  const section = sectionTitle(pathname)
  const hasTrail = typeof trail === 'string' && trail.length > 0

  return (
    <header
      role="banner"
      className="flex h-header w-full flex-none items-center justify-between gap-murmuration-4 border-b border-border-default bg-bg-surface px-murmuration-6"
    >
      <nav aria-label="Breadcrumb" className="min-w-0">
        <ol className="flex items-center gap-murmuration-2 text-murmuration-body-strong text-text-secondary">
          <li className="truncate">
            {hasTrail ? (
              <Link to="/members" className="text-text-secondary hover:text-brand-deep">
                {section}
              </Link>
            ) : (
              section
            )}
          </li>
          {hasTrail && (
            <>
              <li aria-hidden="true" className="text-text-faint">
                /
              </li>
              <li className="truncate font-mono text-murmuration-code text-text-primary">
                {trail}
              </li>
            </>
          )}
        </ol>
      </nav>

      <div className="flex flex-none items-center gap-murmuration-3">
        <div className="relative">
          <input
            type="text"
            disabled
            aria-label="Search members, cases, loans"
            aria-describedby={searchNoteId}
            placeholder="Search members, cases, loans"
            className="h-8 w-[280px] cursor-not-allowed rounded-sm border border-border-input bg-bg-surface-2 px-murmuration-3 text-murmuration-code text-text-muted"
          />
          <span id={searchNoteId} className="sr-only">
            Search is not available yet. Use the filters on the members list.
          </span>
        </div>

        <SignOutMenu />

        {name !== null && (
          // Static text, not a control - the avatar beside it is the only
          // target in this cluster. `text-muted` on white is 5.91:1, which
          // is what lets the role line sit at 11px.
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-murmuration-nav text-text-secondary">{name}</span>
            {role !== null && (
              <span className="truncate text-murmuration-micro text-text-muted">{role}</span>
            )}
          </div>
        )}
      </div>
    </header>
  )
}
